import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Upload, Select, DatePicker, message } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import { DATE_FORMAT } from "../../../../../variables/constants";
import { MODULE_EMPLOYEES } from "../../../../../variables/modules";
import APIService from '../../../../../services/apiservice';
import { showLoading } from '../../../../../redux/layout/layoutState';
import {
    getEmployeeDocumentDetails,
    doEmployeeDocumentCreate,
} from "../../../../../redux/document/documentState";

const DocumentForm = ({employeeId}) => {
  
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee.employeeDetails?.data);
  const recordData = useSelector((state) => state.document.employeeDocumentDetails?.data);
  const [documentFile, setDocumentFile] = useState();

  useEffect(() => {
        dispatch(getEmployeeDocumentDetails({recordId:0, employeeId: employeeId}));
  }, [dispatch, employeeId]);
    
  useEffect(() => {
    if(recordData){			
			form.resetFields();
			form.setFieldsValue(recordData);
    }
  }, [recordData, form]);

  const handleUpload = async (file) => {
		const isValidType =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (!isValidType) {
      message.error('Only PDF, DOC, DOCX files are allowed');
      return Upload.LIST_IGNORE;
    }

    const isLessThan5MB = file.size / 1024 / 1024 < 10;

    if (!isLessThan5MB) {
      message.error('File must be smaller than 10MB');
      return Upload.LIST_IGNORE;
    }

    setDocumentFile(file);
    return false; 
	}

  const onSubmit = async (formData) => {
    try {

      const { file, ...docData } = formData;

      dispatch(showLoading('Uploading...'));
      const result = await APIService.doUploadRequest(`api/v1/console/upload/document`, documentFile);
      const attachment = result.data;

      docData.doc_employee_id = parseInt(employeeId);
      docData.doc_client_id = employee.emp_client_id;
      docData.doc_attachment_id = attachment.att_id;
      docData.doc_date = formData.doc_date?.format(DATE_FORMAT);

      dispatch(doEmployeeDocumentCreate(docData));

    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  return (
    <Form className="form"
      layout="vertical" 
      form={form} 
      onFinish={onSubmit}
    > 
      <div className="mb-4 w-full">
        <FormItem
            name="file"
            iconName="UploadIcon"
            label="Upload Document"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
            if (Array.isArray(e)) return e;
              return e?.fileList;
            }}
            rules={[
            {
              required: true,
              message: 'Please upload the document',
            },
            ]}
          >
            <Upload.Dragger
              accept=".pdf,.doc,.docx"
              maxCount={1}
              beforeUpload={handleUpload}
            >
            <div className="flex w-full items-center gap-2">
              <div className="btn-gradient-emerald px-3 py-1.5 rounded-lg w-[100px]">
                Choose File
              </div>
              <div>
                <p className="text-sm text-left">
                Accepted formats: PDF, DOC, DOCX (Max 5MB)
                </p>
              </div>
            </div>
            <div className="w-full p-6">
              <p className="text-[18px] text-center text-gray-500">
                Click or drag file to this area to upload
              </p>
            </div>
            </Upload.Dragger>
          </FormItem>
      </div>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem className="font-medium mb-2"
          name="doc_type_id"
          label="Document Type"
          rules={[requiredField()]}
        >
            <Select id="doc_type_id"
              className="input-field"
              showSearch
              allowClear={true}
              options={recordData?.documentTypeList}
            /> 
        </FormItem>
        <FormItem className="font-medium mb-2"
          name="doc_title"
          label="Document Title"
          rules={[requiredField()]}
        >
            <Input type="text" 
              id="doc_title"
              className="input-field"
              maxLength={50}
            />
        </FormItem>	
        <FormItem className="font-medium mb-2"
          name="doc_date"
          label="Date Issued"
          rules={[requiredField()]}
        >
            <DatePicker id="doc_date"
              format={DATE_FORMAT}
              className="input-field"
              style={{
                width: "100%",
              }}
            />
        </FormItem>
        <FormItem className="font-medium mb-2 col-span-1 md:col-span-2"
          name="doc_description"
          label="Document Description"
        >
            <TextArea
                allowClear
                rows={5}
                className={`
                    resize-none bg-transparent pr-3 text-[13px] sm:text-[14px] 
                    placeholder:text-muted-foreground
                    disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50
                    border-gray-200 dark:!border-white/10 dark:text-white
                `}
            />
        </FormItem>	
      </div>
      

      <div className="w-full flex justify-end">
          <CrudButtons
            mode="edit"
            recordId={0}
            isModal={true}
            modalId="upload_employee_document"
            cancelLabel="Cancel"
            moduleId={MODULE_EMPLOYEES}
          />
      </div>
    </Form>
  );
};

export default DocumentForm;
